// src/pages/reports/CustomerReport.js
import React, { useState, useEffect } from "react";
import {
  getCustomerReport,
  exportCustomerReport,
  getCustomers,
  getCompanySettings,
} from "../../api/tenant/report.api";
import { toast } from "react-toastify";
import { FaBuilding, FaCalendarAlt, FaFilePdf, FaFileCsv, FaUser, FaChartLine } from "react-icons/fa";

const CustomerReport = () => {
  const [customerData, setCustomerData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split("T")[0]);

  // Fetch company settings
  const fetchCompanySettings = async () => {
    try {
      const response = await getCompanySettings();
      if (response.data?.success) {
        setCompanySettings(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching company settings:", error);
    }
  };

  // Fetch customers list for dropdown
  const fetchCustomers = async () => {
    try {
      const response = await getCustomers();
      console.log("Customers response:", response);
      
      let customerList = [];
      
      if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
        customerList = response.data.data.data;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        customerList = response.data.data;
      } else if (response?.data && Array.isArray(response.data)) {
        customerList = response.data;
      } else if (Array.isArray(response)) {
        customerList = response;
      }
      
      console.log("Customer list extracted:", customerList);
      setCustomers(customerList);
      
      if (customerList.length === 0) {
        toast.info("No customers found. Please add customers first.");
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to fetch customers list");
    }
  };

  // ✅ FIXED: Extract invoices from nested structure
  const extractInvoices = (data) => {
    if (!data) return [];
    
    // If data is already an array of invoice objects
    if (Array.isArray(data) && data.length > 0 && data[0].invoice_no) {
      return data;
    }
    
    // If data is nested (like your API response)
    if (Array.isArray(data)) {
      // Check if first element is an array (nested)
      if (data.length > 0 && Array.isArray(data[0])) {
        // Flatten nested arrays
        const flattened = data.flat();
        // Filter out objects that look like invoices (have invoice_no)
        return flattened.filter(item => item && typeof item === 'object' && item.invoice_no);
      }
      // Check if items are invoice objects
      if (data.length > 0 && data[0] && typeof data[0] === 'object' && data[0].invoice_no) {
        return data;
      }
    }
    
    return [];
  };

  // Fetch Customer report data
  const fetchCustomerReport = async () => {
    if (!selectedCustomerId) {
      toast.warning("Please select a customer");
      return;
    }

    setLoading(true);
    try {
      const response = await getCustomerReport(selectedCustomerId, fromDate, toDate);
      console.log("Full API Response:", response);
      
      if (response.data?.success) {
        const data = response.data.data;
        
        const customer = data.customer;
        // ✅ FIXED: Extract invoices properly
        const rawInvoices = data.invoices || [];
        const invoices = extractInvoices(rawInvoices);
        
        console.log("Extracted invoices:", invoices);
        
        const summary = {
          total_invoices: invoices.length,
          total_purchases: invoices.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0),
          total_gst: invoices.reduce((sum, inv) => sum + (parseFloat(inv.gst_amount) || 0), 0),
          avg_invoice_value: invoices.length > 0 ? invoices.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0) / invoices.length : 0,
          outstanding_amount: invoices.reduce((sum, inv) => sum + (parseFloat(inv.balance_amount) || 0), 0),
        };
        
        setCustomerData({ customer, summary, invoices });
      } else {
        toast.error(response.data?.message || "Failed to fetch customer report");
        setCustomerData(null);
      }
    } catch (error) {
      console.error("Error fetching customer report:", error);
      toast.error(error.response?.data?.message || "Failed to fetch customer report");
      setCustomerData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanySettings();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchCustomerReport();
    }
  }, [selectedCustomerId, fromDate, toDate]);

  // Export handlers
  const handleExportPDF = async () => {
    if (!selectedCustomerId) {
      toast.warning("Please select a customer first");
      return;
    }

    setExporting(true);
    try {
      const response = await exportCustomerReport(selectedCustomerId, "pdf", fromDate, toDate);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `customer_report_${customerData?.customer?.name || "customer"}_${fromDate}_to_${toDate}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedCustomerId) {
      toast.warning("Please select a customer first");
      return;
    }

    setExporting(true);
    try {
      const response = await exportCustomerReport(selectedCustomerId, "csv", fromDate, toDate);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `customer_report_${customerData?.customer?.name || "customer"}_${fromDate}_to_${toDate}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return "₹0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const customer = customerData?.customer || null;
  const summary = customerData?.summary || {
    total_invoices: 0,
    total_purchases: 0,
    total_gst: 0,
    avg_invoice_value: 0,
    outstanding_amount: 0,
  };
  const invoices = customerData?.invoices || [];

  return (
    <div className="container-fluid px-4 py-3">
      {/* Company Header */}
      {companySettings && (
        <div className="card mb-4 shadow-sm border-0">
          <div className="card-body py-3">
            <div className="row align-items-center">
              <div className="col-auto">
                {companySettings.logo ? (
                  <img
                    src={companySettings.logo}
                    alt={companySettings.company_name}
                    style={{ height: "60px", width: "auto" }}
                  />
                ) : (
                  <FaBuilding size={50} color="#4361ee" />
                )}
              </div>
              <div className="col">
                <h3 className="mb-0 fw-bold" style={{ color: companySettings.primary_color || "#4361ee" }}>
                  {companySettings.company_name || "Business Management Pro"}
                </h3>
                {companySettings.brand_tagline && (
                  <p className="text-muted mb-0">{companySettings.brand_tagline}</p>
                )}
                <small className="text-muted">
                  {companySettings.company_email} | {companySettings.company_phone}
                </small>
              </div>
              <div className="col-auto text-end">
                <div className="text-muted small">
                  <strong>GST:</strong> {companySettings.gst_number || "N/A"}<br />
                  <strong>PAN:</strong> {companySettings.pan_number || "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header and Filters */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-0">Customer Report</h2>
          <p className="text-muted mb-0">
            <FaCalendarAlt className="me-1" size={12} />
            {fromDate} to {toDate}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <select
            className="form-select"
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            style={{ width: "250px" }}
          >
            <option value="">Select Customer</option>
            {customers.length > 0 ? (
              customers.map((cust) => (
                <option key={cust.id} value={cust.id}>
                  {cust.name} {cust.mobile ? `- ${cust.mobile}` : ""}
                </option>
              ))
            ) : (
              <option value="" disabled>No customers available</option>
            )}
          </select>
          <input
            type="date"
            className="form-control"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ width: "150px" }}
          />
          <input
            type="date"
            className="form-control"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ width: "150px" }}
          />
          <button
            onClick={fetchCustomerReport}
            className="btn btn-primary"
            disabled={loading || !selectedCustomerId}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="mb-4">
        <button
          onClick={handleExportPDF}
          className="btn btn-danger me-2"
          disabled={exporting || !customerData || !selectedCustomerId}
        >
          <FaFilePdf className="me-1" />
          {exporting ? "Exporting..." : "Export PDF"}
        </button>
        <button
          onClick={handleExportCSV}
          className="btn btn-success"
          disabled={exporting || !customerData || !selectedCustomerId}
        >
          <FaFileCsv className="me-1" />
          Export CSV
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Fetching customer data...</p>
        </div>
      )}

      {/* Report Content */}
      {!loading && customer && (
        <>
          {/* Customer Information Card */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <FaUser className="me-2" />
                Customer Information
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-2">
                  <strong>Name:</strong> {customer.name || "N/A"}
                </div>
                <div className="col-md-4 mb-2">
                  <strong>Email:</strong> {customer.email || "N/A"}
                </div>
                <div className="col-md-4 mb-2">
                  <strong>Mobile:</strong> {customer.mobile || "N/A"}
                </div>
                <div className="col-md-4 mb-2">
                  <strong>GST Number:</strong> {customer.gst_number || "N/A"}
                </div>
                <div className="col-md-4 mb-2">
                  <strong>Status:</strong>
                  <span className={`badge ms-2 ${customer.status === "active" ? "bg-success" : "bg-secondary"}`}>
                    {customer.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="col-md-12">
                  <strong>Address:</strong> {customer.address || "N/A"}
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-3 mb-3">
              <div className="card bg-primary text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Total Invoices</h6>
                  <h3 className="mb-0">{summary.total_invoices || 0}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-success text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Total Purchases</h6>
                  <h3 className="mb-0">{formatCurrency(summary.total_purchases)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-info text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Total GST</h6>
                  <h3 className="mb-0">{formatCurrency(summary.total_gst)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-warning text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Outstanding Amount</h6>
                  <h3 className="mb-0">{formatCurrency(summary.outstanding_amount)}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Details Table - Clean Design */}
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">Invoice Details</h5>
            </div>
            <div className="card-body p-0">
              {invoices.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ fontSize: "14px" }}>
                    <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                      <tr>
                        <th style={{ padding: "12px 16px" }}>Invoice No</th>
                        <th style={{ padding: "12px 16px" }}>Date</th>
                        <th style={{ padding: "12px 16px" }} className="text-end">Total Amount</th>
                        <th style={{ padding: "12px 16px" }} className="text-end">GST Amount</th>
                        <th style={{ padding: "12px 16px" }}>Status</th>
                        <th style={{ padding: "12px 16px" }} className="text-end">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice, index) => (
                        <tr key={index} style={{ borderBottom: "1px solid #e9ecef" }}>
                          <td style={{ padding: "12px 16px" }}>
                            <span className="fw-semibold">{invoice.invoice_no || "N/A"}</span>
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString("en-IN") : "-"}
                          </td>
                          <td style={{ padding: "12px 16px" }} className="text-end fw-semibold">
                            {formatCurrency(invoice.total_amount)}
                          </td>
                          <td style={{ padding: "12px 16px" }} className="text-end">
                            {formatCurrency(invoice.gst_amount)}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <span className={`badge ${invoice.payment_status === "paid" ? "bg-success" : invoice.payment_status === "partial" ? "bg-warning" : "bg-danger"}`}>
                              {(invoice.payment_status || "PENDING").toUpperCase()}
                            </span>
                          </td>
                          <td style={{ padding: "12px 16px" }} className="text-end fw-bold text-danger">
                            {formatCurrency(invoice.balance_amount || invoice.total_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                      <tr>
                        <td colSpan="2" style={{ padding: "12px 16px" }}>TOTAL</td>
                        <td className="text-end" style={{ padding: "12px 16px" }}>
                          {formatCurrency(summary.total_purchases)}
                        </td>
                        <td className="text-end" style={{ padding: "12px 16px" }}>
                          {formatCurrency(summary.total_gst)}
                        </td>
                        <td style={{ padding: "12px 16px" }}></td>
                        <td className="text-end text-danger" style={{ padding: "12px 16px" }}>
                          {formatCurrency(summary.outstanding_amount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="text-muted">
                    <i className="fas fa-info-circle me-2"></i>
                    No invoices found for this customer in the selected date range
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* No Data State */}
      {!loading && !customer && selectedCustomerId && (
        <div className="alert alert-info text-center">
          <i className="fas fa-info-circle me-2"></i>
          No data available for the selected customer and date range
        </div>
      )}

      {/* No Selection State */}
      {!loading && !selectedCustomerId && (
        <div className="alert alert-info text-center">
          <i className="fas fa-info-circle me-2"></i>
          Please select a customer to view the report
        </div>
      )}
    </div>
  );
};

export default CustomerReport;